"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileText, Database, Link2, ArrowUpRight } from "lucide-react";
import { useLiveBlock } from "@/indexedDB/hooks/useLiveBlock";
import { postWithAuth } from "@/lib/api-helpers";
import { cn } from "@/lib/utils";
import { renderIcon } from "./editor/renderIcon";
import { BlockContentMini, useShallowSubtree } from "./BlockContentMini";
import { buildNoteHref } from "@/utils/noteUrl";
import type { Block, IPage } from "@/types/block";
import type { ViewCollection } from "@/types/board";

interface BlockPreviewCardProps {
  blockId: string;
  children: React.ReactNode;
  enabled?: boolean;
}

/**
 * Two render targets share the same preview internals:
 *  - "card"  → the floating hover chip (260×180, fixed, fade at the bottom)
 *  - "panel" → a full-height side panel (taller cover, larger title,
 *              scrollable body) used by the search modal's preview pane.
 */
export type PreviewVariant = "card" | "panel";

// Fixed card dimensions — stays constant whether a cover is shown or not.
const CARD_WIDTH = 260;
const CARD_HEIGHT = 180;
const COVER_HEIGHT = 48;
const HIDE_DELAY_MS = 150;

// Panel variant sizing — larger than the hover card so it reads like a
// real page preview rather than a thumbnail.
const PANEL_COVER_HEIGHT = 120;
const PANEL_MAX_PAGE_CHILDREN = 24;

export const BlockPreviewCard: React.FC<BlockPreviewCardProps> = ({
  blockId,
  children,
  enabled = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0, opacity: 0 });
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Wrapper uses display:contents so it doesn't generate its own box —
  // we measure the actual chip via its first element child instead.
  const wrapperRef = useRef<HTMLSpanElement>(null);

  /**
   * Returns the bounding rect of the chip we're hovering over.  We can't
   * use `e.currentTarget.getBoundingClientRect()` because the wrapper has
   * `display: contents` and therefore no box — the rect would be 0×0 at
   * the document origin, anchoring the card to the top-left corner.
   */
  const measureTriggerRect = (): DOMRect | null => {
    const firstChild = wrapperRef.current?.firstElementChild as
      | HTMLElement
      | null;
    return firstChild?.getBoundingClientRect() ?? null;
  };

  // Subscribe to the block live-query only while hovered to avoid one
  // subscription per chip on the page.
  const block = useLiveBlock(isHovered ? blockId : null);

  const handleMouseEnter = () => {
    if (!enabled) return;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const rect = measureTriggerRect();
    if (!rect) return;
    setTriggerRect(rect);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setIsHovered(false);
    setTriggerRect(null);
    setPosition((p) => ({ ...p, opacity: 0 }));
  };

  // Anchor to the LEFT edge of the trigger (start of block), below it.
  // Card size is fixed, so we use the constants directly for edge correction —
  // this also lets us position correctly on the very first render before the
  // ref-measured DOMRect is available.
  useEffect(() => {
    if (!isHovered || !triggerRect) return;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = triggerRect.left;
    let y = triggerRect.bottom + 8;

    if (x + CARD_WIDTH > viewportWidth - 16) x = viewportWidth - CARD_WIDTH - 16;
    if (x < 16) x = 16;

    if (y + CARD_HEIGHT > viewportHeight - 16) {
      const above = triggerRect.top - CARD_HEIGHT - 8;
      y = above < 16 ? Math.max(16, viewportHeight - CARD_HEIGHT - 16) : above;
    }

    setPosition({ x, y, opacity: 1 });
  }, [isHovered, triggerRect]);

  // Dismiss on scroll to avoid the card lingering in a stale position.
  useEffect(() => {
    if (!isHovered) return;
    const onScroll = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setIsHovered(false);
      setTriggerRect(null);
      setPosition((p) => ({ ...p, opacity: 0 }));
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, [isHovered]);

  return (
    <>
      <span
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="contents"
      >
        {children}
      </span>

      {isHovered && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={cardRef}
            className="fixed z-[1000] pointer-events-none rounded-xl shadow-lg border border-component bg-modal overflow-hidden transition-opacity duration-150 flex flex-col"
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              width: `${CARD_WIDTH}px`,
              height: `${CARD_HEIGHT}px`,
              opacity: position.opacity,
            }}
          >
            <PreviewContent block={block} />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-10 backdrop-blur-[3px] [mask-image:linear-gradient(to_top,black,transparent)] bg-gradient-to-t from-[#f8f8f7] to-transparent dark:from-[#202020] dark:to-transparent"
            />
          </div>,
          document.body,
        )}
    </>
  );
};

// ─── Reusable preview (side panel) ─────────────────────────────────────────

// Pages surfaced by search (Meilisearch) aren't guaranteed to live in
// IndexedDB, so the panel can't rely on useLiveBlock alone. Cache blocks
// fetched from the API at module scope so arrowing back and forth through
// results doesn't refetch the same page each time.
const previewBlockCache = new Map<string, Block>();

/**
 * Resolve a block for preview: prefer the live IDB record, fall back to a
 * one-shot API fetch (cached) for blocks that were never loaded locally.
 */
function useBlockForPreview(blockId: string | null): Block | undefined {
  const live = useLiveBlock(blockId) as Block | undefined;
  const [fetched, setFetched] = useState<Block | undefined>(() =>
    blockId ? previewBlockCache.get(blockId) : undefined,
  );

  useEffect(() => {
    if (!blockId) {
      setFetched(undefined);
      return;
    }
    const cached = previewBlockCache.get(blockId);
    if (cached) {
      setFetched(cached);
      return;
    }
    setFetched(undefined);
    // IDB already has it (or is about to via the live query) — skip the fetch.
    if (live) return;

    let cancelled = false;
    void (async () => {
      try {
        const res = (await postWithAuth("/api/blocks/get-many", {
          blockIds: [blockId],
        })) as { success?: boolean; blocks?: Block[] };
        const block =
          res?.success && Array.isArray(res.blocks) ? res.blocks[0] : undefined;
        if (block) {
          previewBlockCache.set(blockId, block);
          if (!cancelled) setFetched(block);
        }
      } catch {
        // Network/permission failure — the panel just keeps its skeleton.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blockId, live]);

  return live ?? fetched;
}

/**
 * Standalone preview for a block id — no hover/portal chrome. Used by the
 * search modal's side panel. Pass `blockId = null` to show the empty state.
 *
 * In the "panel" variant the content sits inside a framed card (rounded
 * border + shadow, inset by the parent's padding) with
 * link / open actions floating over the cover.
 */
export const BlockPreview: React.FC<{
  blockId: string | null | undefined;
  variant?: PreviewVariant;
  /** Invoked by the "open" action (panel variant). Omit to hide it. */
  onOpen?: () => void;
}> = ({ blockId, variant = "panel", onOpen }) => {
  const block = useBlockForPreview(blockId ?? null);

  if (variant !== "panel") {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden">
        {blockId ? <PreviewContent block={block} variant={variant} /> : <PreviewEmpty />}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[400px] max-h-[400px] h-[400px] w-full flex-col overflow-hidden rounded-xl border border-component bg-modal shadow-sm">
      {blockId ? (
        <>
          <PanelActions blockId={blockId} block={block} onOpen={onOpen} />
          <PreviewContent block={block} variant="panel" />
        </>
      ) : (
        <PreviewEmpty />
      )}
    </div>
  );
};

/**
 * Floating action pill over the cover: copy-link + open. Mirrors the two
 * icons shown at the top-right of the search preview.
 */
const PanelActions: React.FC<{ blockId: string; block?: Block | null; onOpen?: () => void }> = ({
  blockId,
  block,
  onOpen,
}) => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      await navigator.clipboard.writeText(`${origin}${buildNoteHref(blockId, (block?.value as IPage)?.title, block?.blockType)}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  const btn =
    "flex h-7 w-7 items-center justify-center text-gray-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover-component  transition-colors";

  return (
    <div className="absolute right-3 top-3 z-10 flex items-center overflow-hidden rounded-lg border border-gray-200 dark:border-white/15 bg-white/90 dark:bg-black/50 shadow-sm backdrop-blur-md">
      <button
        type="button"
        onClick={copyLink}
        title={copied ? "Copied!" : "Copy link"}
        aria-label="Copy link"
        className={btn}
      >
        <Link2 className="h-3.5 w-3.5" />
      </button>
      {onOpen ? (
        <>
          <span className="h-4 w-px bg-gray-200 dark:bg-white/15" />
          <button
            type="button"
            onClick={onOpen}
            title="Open page"
            aria-label="Open page"
            className={btn}
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </>
      ) : null}
    </div>
  );
};

const PreviewEmpty: React.FC = () => (
  <div className="flex flex-1 w-full flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
    <FileText className="h-6 w-6 opacity-40" />
    <p className="text-xs">Hover a result to preview it here</p>
  </div>
);

// ─── Content dispatch ──────────────────────────────────────────────────────

const PreviewContent: React.FC<{
  block: Block | undefined;
  variant?: PreviewVariant;
}> = ({ block, variant = "card" }) => {
  if (!block) {
    return <PreviewLoading variant={variant} />;
  }
  if (block.blockType === "page") {
    return <PagePreview block={block} variant={variant} />;
  }
  if (block.blockType === "collection_view") {
    return <CollectionPreview block={block} variant={variant} />;
  }
  return (
    <div className="p-3 text-xs text-muted-foreground">
      Preview not available for this block type.
    </div>
  );
};

/**
 * Neutral placeholder strip shown in the panel when a page/board has no
 * cover image, so the framed card always keeps a consistent header zone for
 * the floating actions to sit over (mirrors the empty cover area).
 */
const CoverFallback: React.FC = () => (
  <div
    className="w-full shrink-0 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-[hsl(0deg_0%_16%)] dark:to-[hsl(0deg_0%_13%)]"
    style={{ height: PANEL_COVER_HEIGHT }}
  />
);

const PreviewLoading: React.FC<{ variant?: PreviewVariant }> = ({
  variant = "card",
}) => {
  if (variant === "panel") {
    return (
      <>
        <CoverFallback />
        <div className="flex-1 px-4 pt-4 space-y-3 animate-pulse">
          <div className="h-8 w-8 rounded-md bg-gray-200 dark:bg-gray-700" />
          <div className="h-5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="space-y-2 pt-2">
            <div className="h-2.5 w-full bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-2.5 w-11/12 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-2.5 w-9/12 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </>
    );
  }
  return (
    <div className="flex-1 p-3 space-y-2 animate-pulse">
      <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded mt-3" />
      <div className="h-2 w-11/12 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-2 w-9/12 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  );
};

// ─── Shared shell pieces ───────────────────────────────────────────────────

const Cover: React.FC<{ url: string; height?: number }> = ({
  url,
  height = COVER_HEIGHT,
}) => (
  <div
    className="w-full shrink-0 bg-gray-100 dark:bg-gray-800 overflow-hidden"
    style={{ height }}
  >
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img src={url} alt="" className="w-full h-full object-cover" />
  </div>
);

const Header: React.FC<{
  icon?: string;
  fallbackIcon: React.ReactNode;
  title: string;
  variant?: PreviewVariant;
}> = ({ icon, fallbackIcon, title, variant = "card" }) => {
  const isPanel = variant === "panel";
  const dim = isPanel ? 34 : 20;
  const fontSize = isPanel ? 28 : 18;
  return (
    <div className={cn("shrink-0", isPanel ? "px-4 pt-4" : "px-3 pt-2.5")}>
      <span
        className="flex items-center justify-start"
        style={{ width: dim, height: dim, fontSize }}
      >
        {icon
          ? renderIcon(icon, { style: { width: dim, height: dim, fontSize } })
          : fallbackIcon}
      </span>
      <div
        className={cn(
          "mt-1 font-semibold text-gray-900 dark:text-gray-100 truncate",
          isPanel ? "text-xl" : "text-[13px]",
        )}
      >
        {title}
      </div>
    </div>
  );
};

const MAX_PAGE_CHILDREN = 10;

const PagePreview: React.FC<{ block: Block; variant?: PreviewVariant }> = ({
  block,
  variant = "card",
}) => {
  const isPanel = variant === "panel";
  const value = block.value as IPage;
  // Runtime field is `coverURL` (see notes/[noteId]/page.tsx + notes/layout.tsx).
  // The IPage type says `coverUrl` but production data uses `coverURL` — read
  // both so we're resilient to either spelling on the document.
  const cover =
    (value as { coverURL?: string }).coverURL ?? value.coverUrl ?? null;
  const title = value.title || "New page";

  const childIds = (block.blockIds ?? []).slice(
    0,
    isPanel ? PANEL_MAX_PAGE_CHILDREN : MAX_PAGE_CHILDREN,
  );

  // useShallowSubtree pulls the top-level children PLUS their descendants
  // up to a few levels deep (IDB → API fallback per level).  That's what
  // lets list/task items render properly — their text lives in nested
  // sibling blocks (taskItem → paragraph → text), not inline.
  const { loading, lookup } = useShallowSubtree(childIds, 4);

  // Resolve the top-level children synchronously from the same lookup, so
  // the body re-renders progressively as deeper levels arrive.  Skip nested
  // collection_view blocks — board-shaped data inside a tiny page preview
  // adds noise without adding signal.
  const topChildren = childIds
    .map((id) => lookup(id))
    .filter((b): b is Block => Boolean(b))
    .filter((b) => b.blockType !== "collection_view");

  return (
    <>
      {cover ? (
        <Cover
          url={cover}
          height={isPanel ? PANEL_COVER_HEIGHT : COVER_HEIGHT}
        />
      ) : isPanel ? (
        <CoverFallback />
      ) : null}
      <Header
        icon={value.icon}
        fallbackIcon={
          <FileText size={isPanel ? 28 : 18} className="text-muted-foreground" />
        }
        title={title}
        variant={variant}
      />
      <div
        className={cn(
          "flex-1 min-h-0",
          isPanel
            ? "px-4 pt-3 pb-6 overflow-hidden"
            : "px-3 pt-2 pb-3 overflow-hidden",
        )}
      >
        {topChildren.length === 0 ? (
          loading ? <RowSkeleton /> : <div />
        ) : (
          <div className={isPanel ? "space-y-2" : "space-y-1.5"}>
            {topChildren.map((c) => (
              <div key={c._id} className="min-w-0">
                <BlockContentMini block={c} lookup={lookup} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Collection-view preview — generic mock board (no real rows) ──────────
// We deliberately don't load rows here: a board-link hover should hint
// "this is a board", not preview specific data. Real rows for nested boards
// inside a page preview are also intentionally skipped (filtered out of
// PagePreview's topChildren list).

const CollectionPreview: React.FC<{
  block: Block;
  variant?: PreviewVariant;
}> = ({ block, variant = "card" }) => {
  const isPanel = variant === "panel";
  const value = block.value as ViewCollection;
  const cover = value.coverURL || null;
  const title = value.title || "New Board";

  return (
    <>
      {cover ? (
        <Cover
          url={cover}
          height={isPanel ? PANEL_COVER_HEIGHT : COVER_HEIGHT}
        />
      ) : isPanel ? (
        <CoverFallback />
      ) : null}
      <Header
        icon={value.icon}
        fallbackIcon={
          <Database size={isPanel ? 28 : 18} className="text-muted-foreground" />
        }
        title={title}
        variant={variant}
      />
      <div
        className={cn(
          "flex-1 min-h-0",
          isPanel ? "px-4 pt-3 pb-6" : "px-3 pt-2 pb-3",
        )}
      >
        <MockBoardSkeleton compact={!isPanel && !!cover} />
      </div>
    </>
  );
};

/**
 * Stylised mini board mockup — a header strip with two placeholders + a
 * blue accent button, then a 3×4 grid of placeholder rows with subtle
 * column dividers. Purely decorative.
 */
const MockBoardSkeleton: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  // Row count drops from 4 → 2 in compact mode so a cover-shrunk container
  // doesn't crush each row to single-pixel heights. Column count stays at 3
  // — that's the visual "boardness" signal.
  const rowIndexes = compact ? [0, 1] : [0, 1, 2, 3];
  return (
    <div className="w-full h-full rounded border border-component overflow-hidden flex flex-col">
      {/* Header row */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-component">
        <div className="h-1.5 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-1.5 w-6 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="ml-auto h-3 w-10 bg-blue-500 rounded-sm" />
      </div>
      {/* Body rows + columns */}
      <div className="flex-1 grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700">
        {[0, 1, 2].map((col) => (
          <div key={col} className="flex flex-col">
            {rowIndexes.map((row) => (
              <div
                key={row}
                className="flex-1 flex items-center px-2 border-b border-component last:border-b-0"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-600 mr-1.5" />
                <span className="h-1.5 flex-1 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const RowSkeleton: React.FC = () => (
  <div className="space-y-1.5">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex items-center gap-1.5">
        <div className="h-2.5 w-2.5 rounded-sm bg-gray-200 dark:bg-gray-700" />
        <div className="h-2 flex-1 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    ))}
  </div>
);

export default BlockPreviewCard;
